import { Suspense } from 'react';
import {
  useProduct,
  useSelectedQuantity,
  useSelectionActions
} from '@/components/product/product-context';
import OptionDropdown, {
  DropdownSkeleton
} from '@/components/selectors/OptionDropdown';
import { hasSelectableOptions } from '@/components/product/utils/product-context-helpers';
import offsetBlockClass from '@/utils/offsetBlockClass';
import SelectedVariantDimensions from '../SelectedVariantDimensions';
import VariantSelectorModalLink from '../VariantSelectorModalLink';
import { findSelectorOption, readSelectorSettings } from '../selector-settings';
import { useOptionChoices } from '../use-option-choices';

const QUANTITY_OPTIONS = Array.from({ length: 10 }, (_, index) => ({
  value: index + 1,
  label: String(index + 1)
}));

const SELECT =
  'body-r focus-visible:ring-blue mt-2 block cursor-pointer py-3 pr-3 pl-5 text-black focus-visible:border focus-visible:border-black focus-visible:ring-4';
const QUANTITY_WIDTH = 'w-18.25 lg:w-21';
const PRIMARY_SELECT = `${SELECT} w-full truncate bg-white`;
const QUANTITY_SELECT = `${SELECT} ${QUANTITY_WIDTH}`;

/**
 * A variant selector rendered as a native `<select>`, optionally alongside a
 * quantity dropdown and the selected variant's dimensions.
 *
 * The section, the label and the modal all come from the CMS, so they render in
 * the static shell. Both dropdowns fall back to an inert copy of themselves.
 */
export default function Dropdown({ block, position }) {
  const { productData } = useProduct();
  const { settings, selectorSettings } = readSelectorSettings(block);
  const {
    uid,
    label,
    modal,
    option: optionName,
    show_offset_block_selector
  } = settings;
  const { show_dimensions, show_quantity } = selectorSettings;

  const options = productData?.shopifyProduct?.options ?? [];
  if (!hasSelectableOptions(options)) {
    return null;
  }

  const option = findSelectorOption(productData, optionName, 'drop down');
  if (!option) {
    return null;
  }

  // The selection state keys options by their lowercased name.
  const selectOptionName = option.name.toLowerCase();
  const selectId = `variant-selector-${selectOptionName}`;
  const topSpacingClass =
    show_offset_block_selector === 'multi-block' ? '' : 'mt-2';

  return (
    <section
      id={`${uid}_${position}`}
      data-name="variant-selector"
      className={offsetBlockClass(show_offset_block_selector, 'not-last:mb-8')}
    >
      <div className="flex flex-row gap-4">
        {/* LEFT: Variant dropdown form */}
        <form
          key={option.id}
          className="text-crisp-white w-full"
          data-cy="primary-selector"
        >
          <div className="flex cursor-default justify-between">
            <label htmlFor={selectId} className={`sh12 ${topSpacingClass}`}>
              {label}
            </label>

            <VariantSelectorModalLink modal={modal} />

            {show_dimensions && (
              <Suspense fallback={null}>
                <SelectedVariantDimensions
                  showOffsetBlockSelector={show_offset_block_selector}
                />
              </Suspense>
            )}
          </div>

          <div className="variant-select-wrapper relative mt-2">
            <Suspense
              fallback={<DropdownSkeleton className={PRIMARY_SELECT} />}
            >
              <SelectedDropdown
                id={selectId}
                optionName={selectOptionName}
                optionValues={option.values}
              />
            </Suspense>
          </div>
        </form>

        {/* RIGHT: Quantity selector */}
        {show_quantity && (
          <div className={`flex flex-col ${topSpacingClass}`}>
            <label
              className="qty-selector text-crisp-white sh12"
              htmlFor="accessory"
            >
              <span className="sr-only">Select Accessory Quantity</span>
              Qty:
            </label>
            <div className="flex min-h-12.5">
              <Suspense
                fallback={<DropdownSkeleton className={QUANTITY_SELECT} />}
              >
                <SelectedQuantity />
              </Suspense>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Reads the selected variant and selection actions, so it needs to stay inside
 * a Suspense boundary.
 */
const SelectedDropdown = ({ id, optionName, optionValues }) => {
  const { choices, value, onChange } = useOptionChoices(
    optionName,
    optionValues
  );

  return (
    <OptionDropdown
      id={id}
      name={id}
      options={choices}
      value={value}
      onChange={onChange}
      className={PRIMARY_SELECT}
      data-cy={`option-dropdown-${optionName}`}
    />
  );
};

/**
 * Reads the selected quantity and selection actions, so it needs to stay inside
 * a Suspense boundary.
 */
const SelectedQuantity = () => {
  const { updateQuantity } = useSelectionActions();
  const { quantity } = useSelectedQuantity();

  return (
    <OptionDropdown
      id="accessory"
      name="accessory"
      options={QUANTITY_OPTIONS}
      value={quantity}
      onChange={(e) => updateQuantity(parseInt(e.target.value, 10))}
      className={QUANTITY_SELECT}
      dataCy="variant-quantity-selector"
    />
  );
};
